import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight, Car, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ThankYouPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Auto-scroll to top
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex flex-col" data-testid="thank-you-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D6] py-4">
        <div className="container-custom">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#2C4A3B] rounded-full flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-xl md:text-2xl font-bold text-[#2C4A3B]">YesApproved</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-24 h-24 bg-[#2C4A3B] rounded-full flex items-center justify-center mx-auto mb-8 animate-fade-in">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#2C4A3B] mb-6 animate-fade-in stagger-1">
            Thank You for Your Application!
          </h1>
          
          <p className="text-xl text-[#5A5A5A] mb-8 animate-fade-in stagger-2">
            We've received your pre-approval request and our team is already working on finding the best financing options for you.
          </p>

          <div className="bg-white rounded-2xl border border-[#E5E0D6] p-8 mb-8 animate-fade-in stagger-3" data-testid="next-steps-card">
            <h2 className="font-heading text-2xl font-semibold text-[#2C4A3B] mb-6">What Happens Next?</h2>
            
            <div className="space-y-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#C07756] flex items-center justify-center text-white font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C4A3B] mb-1">Application Review</h3>
                  <p className="text-[#5A5A5A]">Our financing team will review your application within 24 hours</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#C07756] flex items-center justify-center text-white font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C4A3B] mb-1">We'll Contact You</h3>
                  <p className="text-[#5A5A5A]">A friendly team member will reach out to discuss your options and answer any questions</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#C07756] flex items-center justify-center text-white font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C4A3B] mb-1">Choose Your Vehicle</h3>
                  <p className="text-[#5A5A5A]">Once approved, browse our inventory and pick your perfect ride!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F0EBE0] rounded-2xl p-6 mb-8 animate-fade-in stagger-4" data-testid="contact-card">
            <p className="text-[#2C4A3B] font-medium mb-4">Need to reach us sooner?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a href="tel:6048610021" className="flex items-center gap-2 text-[#C07756] hover:text-[#A66548] transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-medium">(604) 861-0021</span>
              </a>
              <a href="mailto:info@yesapproved.ca" className="flex items-center gap-2 text-[#C07756] hover:text-[#A66548] transition-colors">
                <Mail className="w-5 h-5" />
                <span className="font-medium">info@yesapproved.ca</span>
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-5">
            <Button
              onClick={() => navigate('/')}
              className="btn-primary"
              data-testid="back-home-btn"
            >
              <Home className="mr-2 w-5 h-5" /> Back to Home
            </Button>
            <Button
              onClick={() => navigate('/#inventory')}
              variant="outline"
              className="btn-secondary"
              data-testid="browse-vehicles-btn"
            >
              Browse Vehicles <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2C4A3B] text-white py-8">
        <div className="container-custom text-center">
          <p className="text-white/60 text-sm">
            © 2024 YesApproved.ca / Cars21 Motors. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
